// Pure helpers for the Document Intelligence sources/files UI. No imports so they run under `deno test`.

export interface IngestedFileLike {
  id: string;
  source_id: string;
  provider: string;
  file_name: string;
  extraction_status: string;
  embedding_status: string;
  removed_at: string | null;
  chunk_count: number;
}

export type PipelineStage = 'removed' | 'failed' | 'analyzed' | 'extracted' | 'pending' | 'unsupported';

export interface StatusChip {
  stage: PipelineStage;
  label: string;
  tone: 'default' | 'secondary' | 'destructive' | 'outline';
}

/** Pipeline chip for a file. Removed wins over everything; failed over the rest. */
export function fileStatusChip(f: Pick<IngestedFileLike, 'extraction_status' | 'removed_at'>): StatusChip {
  if (f.removed_at) return { stage: 'removed', label: 'Removed', tone: 'outline' };
  switch (f.extraction_status) {
    case 'failed': return { stage: 'failed', label: 'Failed', tone: 'destructive' };
    case 'analyzed': return { stage: 'analyzed', label: 'Analyzed', tone: 'default' };
    case 'extracted': return { stage: 'extracted', label: 'Extracted', tone: 'secondary' };
    case 'unsupported': return { stage: 'unsupported', label: 'Unsupported', tone: 'outline' };
    default: return { stage: 'pending', label: 'Pending', tone: 'secondary' };
  }
}

/** "Searchable by Ask Masira": chunks exist, embedding is ok, and the file is not removed. */
export function isSearchable(f: Pick<IngestedFileLike, 'chunk_count' | 'embedding_status' | 'removed_at'>): boolean {
  return !f.removed_at && f.chunk_count > 0 && f.embedding_status === 'ok';
}

/** Files waiting for analysis: extracted, not removed. */
export function pendingAnalysis<T extends Pick<IngestedFileLike, 'extraction_status' | 'removed_at'>>(files: readonly T[]): T[] {
  return files.filter((f) => f.extraction_status === 'extracted' && !f.removed_at);
}

export type StageFilter = 'all' | 'active' | 'pending' | 'extracted' | 'analyzed' | 'failed' | 'unsupported' | 'removed';

/** Filters by source id ('all' = any) and stage. 'active' (default) hides removed files. */
export function filterFiles<T extends IngestedFileLike>(files: readonly T[], source: string, stage: StageFilter): T[] {
  return files.filter((f) => {
    if (source !== 'all' && f.source_id !== source) return false;
    if (stage === 'all') return true;
    if (stage === 'active') return !f.removed_at;
    return fileStatusChip(f).stage === stage;
  });
}

export function providerLabel(p: string): string {
  if (p === 'google') return 'Google Drive';
  if (p === 'microsoft') return 'Microsoft 365';
  return p;
}

export interface SourceChip {
  label: string;
  provider: string | null;
  removed: boolean;
  sourceId: string | null;
}

/**
 * Chip for a candidate card. Resolved through loaded files by source_document_id.
 * A null link on a reviewed (non-suggested) candidate means its source file was removed.
 * Anything else unresolved (e.g. not-yet-loaded page, non-ingested doc) gets no chip.
 */
export function resolveSourceChip(
  candidate: { source_document_id: string | null; status: string },
  filesById: ReadonlyMap<string, Pick<IngestedFileLike, 'file_name' | 'provider' | 'source_id' | 'removed_at'>>,
): SourceChip | null {
  if (candidate.source_document_id == null) {
    return candidate.status === 'suggested'
      ? null
      : { label: 'Source removed', provider: null, removed: true, sourceId: null };
  }
  const f = filesById.get(candidate.source_document_id);
  if (!f) return null;
  return { label: f.file_name, provider: f.provider, removed: !!f.removed_at, sourceId: f.source_id };
}

/** Candidate source filter: 'all', 'removed' (reviewed, link gone), or an ingest source id. */
export function matchesSourceFilter(chip: SourceChip | null, filter: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'removed') return !!chip && chip.sourceId === null && chip.removed;
  return !!chip && chip.sourceId === filter;
}
